import { Direction } from '@modules/common/types';
import { directionToString } from '@modules/common/utils';
import { MIN_ROW_HEIGHT } from '@modules/dashboard/constants';
import {
  addCellToDashboardLayout,
  getNewCellID,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks/useUpsertDashboard';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DashboardCell } from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useSetDashboardActiveCellId } from './useSetDashboardActiveCellId';

interface useAddTextWidgetParams {
  rowID?: string;
  cellInsertDirection: Direction;
}

export function useAddTextWidget({
  rowID,
  cellInsertDirection,
}: useAddTextWidgetParams) {
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const { upsertDashboard } = useUpsertDashboard();
  const setDashboardActiveCellId = useSetDashboardActiveCellId();
  const cellMinimumHeightsByRowID = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID,
  );

  const addTextContent = async (
    textContent?: string,
  ): Promise<DashboardCell> => {
    const clonedMetadata = cloneDeep(metadata);

    const contentID = uuidv4();
    const cell = {
      id: getNewCellID(),
      columnSpan: 12,
      contentID,
    };

    const { destinationRowID } = addCellToDashboardLayout({
      cell,
      rowID,
      cellInsertDirection,
      layout: clonedMetadata.layout,
    });

    dispatch({
      type: DashboardActionType.SET_METADATA,
      payload: clonedMetadata,
    });

    dispatch({
      type: DashboardActionType.SET_VISUAL,
      payload: {
        cellMinimumHeightsByRowID: {
          ...cellMinimumHeightsByRowID,
          [destinationRowID]: {
            ...cellMinimumHeightsByRowID[destinationRowID],
            [cell.id]: MIN_ROW_HEIGHT,
          },
        },
      },
    });

    setDashboardActiveCellId(cell.id);

    await upsertDashboard({
      dashboardProps: {
        metadataV2: clonedMetadata,
        addWidget: {
          id: contentID,
          textWidget: {
            text: textContent ?? '',
          },
          cellIdentifier: {
            cellId: cell.id,
            rowId: destinationRowID,
          },
        },
      },
      shouldRefetchDashboard: true,
    });

    trackDashboardEvent({
      eventName: 'Dashboard Add Content',
      placement: directionToString(cellInsertDirection),
      type: 'text',
      from: 'Dashboard Page',
    });

    return cell;
  };

  return addTextContent;
}
